import React, { useMemo } from 'react';
import { Form, InputNumber, Alert, Typography, Row, Col } from 'antd';

const { Text } = Typography;

export const TOTAL_DRUM = 200;

/**
 * Componente compartilhado de quantidades de um tambor (solvente, borra, água, faltando).
 * Usado pelos wizards de Processamento e de Conferência de compra (DRY).
 *
 * Regra de negócio:
 *  - Quando `strict` (default true = conferência de compra): a soma dos 4 componentes
 *    deve ser exatamente 200 L (o tambor comprado é de 200 L). Bloqueia o avanço.
 *  - Quando `!strict` (processamento): a soma NÃO precisa fechar 200 — pode passar ou
 *    ficar menor. O componente apenas AVISA o usuário (não bloqueia).
 *
 * Props:
 *  - form: instância de Form do Ant Design
 *  - prefix: 'processing_' (processamento) ou 'purchase_' (compra) — nome dos campos no payload
 *  - strict: boolean (default true)
 */
const DrumQuantities = ({ form, prefix = 'processing_', strict = true }) => {
  const values = Form.useWatch([], form);

  const total = useMemo(() => {
    const f = ['solvent', 'borra', 'water', 'missing'];
    return f.reduce((sum, key) => sum + (Number(values?.[`${prefix}${key}`]) || 0), 0);
  }, [values, prefix]);

  const ok = total === TOTAL_DRUM;

  // Define o tipo e a mensagem do alerta conforme o modo (strict x flexível)
  let alertType;
  let alertMessage;
  if (ok) {
    alertType = 'success';
    alertMessage = `Total: ${total.toFixed(2).replace('.', ',')} L / ${TOTAL_DRUM} L — soma correta ✓`;
  } else if (strict) {
    alertType = 'error';
    const diff = TOTAL_DRUM - total;
    alertMessage = `Total: ${total.toFixed(2).replace('.', ',')} L / ${TOTAL_DRUM} L — ${diff > 0 ? `faltam ${diff.toFixed(2).replace('.', ',')} L` : `excedeu ${Math.abs(diff).toFixed(2).replace('.', ',')} L`} para fechar ${TOTAL_DRUM} L.`;
  } else if (total > TOTAL_DRUM) {
    alertType = 'warning';
    alertMessage = `Total: ${total.toFixed(2).replace('.', ',')} L — atenção, passou ${(total - TOTAL_DRUM).toFixed(2).replace('.', ',')} L dos ${TOTAL_DRUM} L. Isso é permitido, apenas confira os valores.`;
  } else {
    alertType = 'warning';
    alertMessage = `Total: ${total.toFixed(2).replace('.', ',')} L — atenção, ficou ${(TOTAL_DRUM - total).toFixed(2).replace('.', ',')} L abaixo dos ${TOTAL_DRUM} L. Isso é permitido, apenas confira os valores.`;
  }

  const fields = [
    { key: 'solvent', label: 'Solvente sujo (L)' },
    { key: 'borra', label: 'Borra (L)' },
    { key: 'water', label: 'Água (L)' },
    { key: 'missing', label: 'Faltando (L)' },
  ];

  return (
    <div>
      <Row gutter={16}>
        {fields.map(f => (
          <Col xs={24} sm={12} key={f.key}>
            <Form.Item
              label={f.label}
              name={`${prefix}${f.key}`}
              rules={[{ required: true, message: 'Informe a quantidade.' }]}
            >
              <InputNumber
                min={0}
                step={0.1}
                style={{ width: '100%' }}
                aria-label={f.label}
                decimalSeparator=","
                inputMode="decimal"
              />
            </Form.Item>
          </Col>
        ))}
      </Row>

      <Alert type={alertType} showIcon message={<Text>{alertMessage}</Text>} />
    </div>
  );
};

export default DrumQuantities;
